import {BaseSectionStatus} from './base-section-status'
import type {MergeBoxSectionStatus} from './types'
import type {
  AliveChannels as MergeBoxAliveChannels,
  StackEntryPayload,
  StackEntryServerStatus,
  StackMergeConditionPayload,
} from '../../page-data/payloads/merge-box'
import type {AliveChannels as StatusChecksAliveChannels} from '../../page-data/payloads/status-checks'
import type {StackMergeabilityPageData} from '../../page-data/payloads/stack-mergeability'
import {getConflictsCondition, getFailingRulesConditions, getStackCondition} from '../json-api-helpers'
import type {UseQueryResult} from '@github-ui/react-query'
import {FeatureState} from '@github-ui/pull-requests/page-data/payloads/header'

type AliveChannels = MergeBoxAliveChannels & StatusChecksAliveChannels
export type StackSectionStatusType = 'PASSED' | 'FAILED' | 'BLOCKED' | 'LOADING_MERGE_STATE' | 'PENDING' | 'UNKNOWN'
export type StackSectionEntryStatus =
  | Exclude<StackEntryServerStatus, 'NEEDS_MERGE_STATE'>
  | 'LOADING_MERGE_STATE'
  | 'ERROR_MERGE_STATE'
  | 'BLOCKED_MERGE_STATE'
  | 'UNKNOWN_MERGE_STATE'
  | 'WAITING_ON_PREVIOUS'
export type StackSectionEntryType = Omit<StackEntryPayload, 'status'> & {
  status: StackSectionEntryStatus
  channels: AliveChannels | null
}

const MERGE_STATE_TO_ENTRY_STATE: Record<StackMergeabilityPageData['state'], StackSectionEntryStatus> = {
  MERGEABLE: 'OK',
  UNMERGEABLE: 'BLOCKED_MERGE_STATE',
  UNKNOWN: 'UNKNOWN_MERGE_STATE',
}

const BLOCKED_STATUSES: StackSectionEntryStatus[] = [
  'AWAITING_APPROVAL',
  'BLOCKED_MERGE_STATE',
  'BLOCKED_STACK',
  'CHECKS_FAILING',
  'ERROR_MERGE_STATE',
  'FAILING_RULES',
  'IRRELEVANT_DRAFT',
  'MERGE_CONFLICTS',
]

export class StackSectionStatus extends BaseSectionStatus<StackSectionStatusType> {
  #stackCondition: StackMergeConditionPayload | undefined
  #stackEntries: StackSectionEntryType[] | undefined
  #loadingEntryCount: number | undefined

  override get shouldRender() {
    return this.featureStates.pullRequestStacks === FeatureState.Enabled && this.isStacked
  }

  override get sectionStatus(): StackSectionStatusType {
    const e = this.entry

    let anyUnknown = false
    let anyLoading = false
    let anyPending = false
    this.#loadingEntryCount = 0

    switch (this.stackCondition?.result || 'UNKNOWN') {
      case 'FAILED':
        if (e?.failedSubConditions && e.failedSubConditions.length > 0) {
          // This stack entry has failed to meet some condition
          return 'FAILED'
        } else {
          // A previous entry in the stack has failed to meet some condition
          return 'BLOCKED'
        }
      case 'UNKNOWN':
        return 'UNKNOWN'
      case 'PASSED':
        for (const entry of this.entries || []) {
          switch (entry.status) {
            case 'BLOCKED_MERGE_STATE':
            case 'ERROR_MERGE_STATE':
            case 'IRRELEVANT_DRAFT':
            case 'AWAITING_APPROVAL':
            case 'FAILING_RULES':
            case 'CHECKS_FAILING':
            case 'MERGE_CONFLICTS':
              // If any previous entry is blocked, we are blocked.
              return 'BLOCKED'
            case 'LOADING_MERGE_STATE':
              // If any entry is loading status, show loading state
              this.#loadingEntryCount++
              anyLoading = true
              break
            case 'CHECKS_PENDING':
              anyPending = true
              break
            case 'UNKNOWN_MERGE_STATE':
              // If any previous entry is unknown, we will be unknown
              // unless we encounter a blocked entry further down the
              // stack.
              anyUnknown = true
          }
        }

        if (anyLoading) {
          return 'LOADING_MERGE_STATE'
        }
        if (anyPending) {
          return 'PENDING'
        }
        if (anyUnknown) {
          return 'UNKNOWN'
        }
        return 'PASSED'
    }
  }

  override get mergeBoxStatus(): MergeBoxSectionStatus {
    const sectionStatus = this.sectionStatus

    switch (sectionStatus) {
      case 'BLOCKED':
        return 'FAILED'
      default:
        return sectionStatus
    }
  }

  get isStacked() {
    return !!this.stackCondition?.isStacked
  }

  get entry() {
    return this.entries?.find(e => e.position === 'CURRENT')
  }

  get entries(): StackSectionEntryType[] | undefined {
    if (!this.#stackEntries) {
      const entries = this.stackCondition?.entries
      if (!entries) {
        return undefined
      }

      const queries = this.stackMergeabilityQueries ?? []
      const computed = entries.map((entry, i) => {
        const query = queries[i]
        return {...entry, ...this.#entryStatus(entry, query)}
      })

      // Refine the CURRENT entry's `OK` status using the surrounding
      // mergeRequirements (rules / conflicts / status state), and mark any
      // entry that is itself OK but is blocked by a lower-position entry
      // (i.e. one that merges before it) as `WAITING_ON_PREVIOUS`. This
      // includes AFTER entries with OK status.
      //
      // The Rails `MergeConditions::Stack` payload orders entries by position
      // descending: `[AFTER…, CURRENT, BEFORE…]` (see `@entries.reverse.map`
      // in stack.rb). So we walk the array from the end toward the start —
      // i.e. from the bottom of the stack upward through CURRENT and any
      // AFTER entries — to propagate `priorBlocked` from lower-position
      // entries to higher-position ones, matching merge order.
      for (const e of computed) {
        if (e.position === 'CURRENT' && e.status === 'OK') {
          const refined = this.#refineCurrentEntryStatus()
          if (refined) e.status = refined
        }
      }

      let priorBlocked = false
      for (let i = computed.length - 1; i >= 0; i--) {
        const e = computed[i]!
        if (e.position === 'AFTER' && e.status === 'IRRELEVANT_AFTER') continue
        if (priorBlocked && e.status === 'OK') {
          e.status = 'WAITING_ON_PREVIOUS'
        } else if (BLOCKED_STATUSES.includes(e.status)) {
          priorBlocked = true
        }
      }

      // Stacks merge bottom-up: BEFORE entries merge first (in descending position order),
      // then CURRENT. So if any entry in the stack is currently MERGING,
      // every BEFORE entry that isn't already merged or closed is part of that in-flight merge and
      // should also surface as MERGING.
      // This covers both:
      //   - CURRENT is MERGING → all BEFORE entries are MERGING.
      //   - A BEFORE entry is MERGING → BEFORE entries further down the
      //     stack (earlier in merge order) are also MERGING.
      const anyMerging = computed.some(e => e.status === 'MERGING')
      if (anyMerging) {
        for (const e of computed) {
          if (e.position === 'BEFORE' && e.pull.state !== 'MERGED' && e.pull.state !== 'CLOSED') {
            e.status = 'MERGING'
          }
        }
      }

      this.#stackEntries = computed
    }

    return this.#stackEntries
  }

  get loadingEntryCount() {
    return this.#loadingEntryCount ?? 0
  }

  get stackCondition() {
    if (!this.#stackCondition) {
      this.#stackCondition = getStackCondition(this.mergeRequirements)
    }
    return this.#stackCondition
  }

  // Count of entries in the stack that will be merged if the user merges the current pull request.
  // This includes the current entry (if it's not already merged) and any entries before it in the stack
  // that aren't merged.
  get entriesToMergeCount(): number {
    return (
      this.entries?.filter(e => {
        return (e.position === 'BEFORE' || e.position === 'CURRENT') && e.pull.state !== 'MERGED'
      }).length ?? 0
    )
  }

  #entryStatus(
    entry: StackEntryPayload,
    mergeStateQuery: UseQueryResult<StackMergeabilityPageData> | undefined,
  ): {
    status: StackSectionEntryStatus
    channels: AliveChannels | null
  } {
    if (entry.status !== 'NEEDS_MERGE_STATE') {
      return {status: entry.status, channels: null}
    }

    // Handle async merge state fetch lifecycle for BEFORE entries
    if (!mergeStateQuery || mergeStateQuery.isError) {
      return {status: 'ERROR_MERGE_STATE', channels: null}
    }
    if (mergeStateQuery.isLoading) {
      return {status: 'LOADING_MERGE_STATE', channels: null}
    }

    // The server emits a fine-grained `entryStatus` (AWAITING_APPROVAL,
    // CHECKS_PENDING, CHECKS_FAILING, MERGE_CONFLICTS, FAILING_RULES,
    // MERGING, OK). Prefer that when present.
    let status: StackSectionEntryStatus
    if (mergeStateQuery.data?.entryStatus && mergeStateQuery.data?.entryStatus !== 'NEEDS_MERGE_STATE') {
      status = mergeStateQuery.data?.entryStatus
    } else {
      status = MERGE_STATE_TO_ENTRY_STATE[mergeStateQuery.data?.state || 'UNKNOWN']
    }

    if (mergeStateQuery.data) {
      const channels = {
        ...mergeStateQuery.data.channels,
        ...mergeStateQuery.data.statusCheckChannels,
      }
      return {status, channels}
    } else {
      return {status, channels: null}
    }
  }

  // Derives a fine-grained v2 status for the CURRENT entry from the
  // surrounding `mergeRequirements`. The synchronous Stack merge condition
  // emits `OK` for the current entry whenever there are no stack-specific
  // failures, so we look at sibling conditions (conflicts / rules / overall
  // state) to surface the real reason the PR isn't mergeable.
  //
  // Returns `null` when no refinement applies (PR really is OK).
  #refineCurrentEntryStatus(): StackSectionEntryStatus | null {
    const mr = this.mergeRequirements
    if (!mr) return null

    if (mr.state !== 'UNMERGEABLE') return null

    if (getConflictsCondition(mr)?.result === 'FAILED') {
      return 'MERGE_CONFLICTS'
    }

    const failingRules = getFailingRulesConditions(mr)
    const failedRuleTypes = new Set<string>()
    for (const condition of failingRules) {
      for (const rollup of condition.ruleRollups) {
        if (rollup.result === 'FAILED') failedRuleTypes.add(rollup.ruleType)
      }
    }

    if (failedRuleTypes.size === 0) {
      // Some other condition (user/repo state etc.) is failing — bucket as
      // FAILING_RULES so the existing blocked banner still surfaces.
      return 'FAILING_RULES'
    }

    const checkRuleTypes = ['REQUIRED_STATUS_CHECKS', 'REQUIRED_WORKFLOW_STATUS_CHECKS']
    const onlyChecksFailed = [...failedRuleTypes].every(t => checkRuleTypes.includes(t))
    if (onlyChecksFailed) return 'CHECKS_FAILING'

    // If a review-required rule (PULL_REQUEST) is among the failures, surface
    // it as "Awaiting approval" — even when other rules are also failing —
    // so reviewers see the most actionable status.
    if (failedRuleTypes.has('PULL_REQUEST')) {
      return 'AWAITING_APPROVAL'
    }

    return 'FAILING_RULES'
  }
}
